import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import IntegrationsHeader from '@/components/organisms/Headers/IntegrationsHeader';
import EmailItem from '@/components/organisms/Integrations/Email/components/EmailItem';
import { ListEmptyState } from '@/components/organisms/Integrations/Email/components/ListEmptyState';
import IntegrationContainer from '@/components/pages/Integrations/IntegrationContainer';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import useSegments from '@/segment/useSegments';
import { EmailIntegration, IntegrationType } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

/**
 * EmailList - Main page component for managing email integrations
 *
 * Features:
 * - Displays list of configured email accounts
 * - Shows empty state when no emails are configured
 * - "Add email" button to create new email integration
 *
 * @returns {JSX.Element} The email list page component
 */
const EmailList = () => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { desk } = useDesks(deskId);
  const { integrations, listStatus } = useIntegrations(deskId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  useTitle(t('pages.emails', { 0: desk?.name ?? '-' }));

  useSegments(PageName.INTEGRATIONS);

  // Filter and sort email integrations
  const emailIntegrations = useMemo(
    () =>
      (integrations?.filter((integration) => integration.type === IntegrationType.EMAIL) ||
        []) as EmailIntegration[],
    [integrations]
  );

  return (
    <>
      <IntegrationsHeader />
      <PageContentWrapper readOnly={!canWrite}>
        {emailIntegrations.length === 0 && listStatus !== 'pending' ? (
          <ListEmptyState />
        ) : (
          <div className="mx-auto mb-96 flex max-w-lg min-w-sm flex-col">
            <h2 className="mb-4 px-1 text-lg leading-7 font-semibold">{t('email.title')}</h2>
            <IntegrationContainer>
              {emailIntegrations.map((integration) => (
                <EmailItem key={integration.integration_id} integration={integration} />
              ))}
            </IntegrationContainer>
          </div>
        )}
      </PageContentWrapper>
    </>
  );
};

export default EmailList;
